/**
 * Greeting-name normalization (founder directive, TestFlight build 7,
 * 2026-07-21): provider- and backend-DERIVED display names are reduced to the
 * FIRST name token before being stored or shown — "Ergi Shehu" greets as
 * "Ergi". Names the user typed themselves (NameInput) are kept verbatim:
 * user intent wins, so nothing in this module may be applied to manual input.
 *
 * Tokenization is whitespace-only (`\s+`) — no Latin-script assumptions, so
 * Arabic/RTL names ("محمد عبد الله" → "محمد") reduce correctly.
 */

/** First whitespace-separated token of a name; '' for empty/blank input. */
export function firstNameOf(fullName: string | null | undefined): string {
  return fullName?.trim().split(/\s+/)[0] ?? '';
}

/**
 * Normalizes a displayName coming back from a BACKEND profile.
 *
 * The backend column can hold either:
 *  (a) a provider-derived full name — legacy rows written before this
 *      normalization shipped, plus the server-side seed (userService copies
 *      the Firebase token's full name at user creation); or
 *  (b) a name the user typed into NameInput, synced from another device.
 *
 * There is no provenance flag, so we use identity as the tell: a remote name
 * that EXACTLY matches the auth provider's full displayName is
 * provider-derived and gets reduced; anything else is treated as user-typed
 * and kept verbatim. (Edge case accepted: a user who manually types their
 * exact provider full name is reduced — consistent with the product policy.)
 */
export function normalizeRemoteName(
  remoteName: string | null | undefined,
  providerDisplayName: string | null | undefined,
  isCustom = false,
): string {
  const trimmed = remoteName?.trim() ?? '';
  if (!trimmed) return '';
  // Explicit provenance beats the identity heuristic below. Once the account
  // carries nameIsCustom, the stored name is what the user typed and is kept
  // verbatim — even when it happens to equal the provider's full name.
  if (isCustom) return trimmed;
  const provider = providerDisplayName?.trim() ?? '';
  return provider && trimmed === provider ? firstNameOf(trimmed) : trimmed;
}
